import { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { initializeAnalytics, initializeIntercom } from '@bonusly/analytics';
import { useApplication } from '@bonusly/application';
import { useBreakpointValue } from '@bonusly/design-system';
import { getQueryClient } from '@bonusly/query-client';

import { PUBLIC_PATH_PREFIX } from '../../../app/public-path';
import { useIsBizyEnabledForUser } from '../hooks/use-is-bizy-enabled-for-user';
import { initializeHotJar } from '../lib/hotjar';
import { useIdentityStore } from '../lib/store';
import { refreshToken } from '../lib/token';
import { meQueryKey, useProfileQuery } from '../queries/profile';
import { createConfig } from '../utils/env';
import { parseToken } from '../utils/token';
import { joinPath } from '../utils/url';

interface AuthProviderProps extends PropsWithChildren {
	// Passing a JWT is intended for local development. This will bypass the need
	// to make a request with a cookie, which may not be available in a local dev.
	jwt?: string;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children, jwt: providedJWT }) => {
	const { application } = useApplication();
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const initRef = useRef(false);
	const analyticsInitRef = useRef(false);
	const tokenScopeRef = useRef<string | undefined>();

	// Read from Zustand — populated by the first effect below
	const isCompanyFetched = useIdentityStore((state) => state.isCompanyFetched);
	const token = useIdentityStore((state) => state.token);
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	useEffect(() => {
		if (initRef.current) return;
		initRef.current = true;

		(async () => {
			// The public form serves anonymous visitors who have no session. Skip the
			// token refresh entirely so a missing JWT never triggers the login redirect
			// below for `/public/*`. Marking init complete keeps every authenticated path
			// unchanged — this guard is inert off `/public/*`.
			if (window.location.pathname.startsWith(PUBLIC_PATH_PREFIX)) {
				return;
			}

			if (providedJWT) {
				useIdentityStore.setState({ token: providedJWT });
				return;
			}

			const jwt = await refreshToken();

			// If no JWT is available then the user is not logged in because of either an expired or missing session
			// token. In this case we need to manually redirect to the login page.
			if (!jwt) {
				const currentUrl = window.location.href;
				const redirectParam = encodeURIComponent(currentUrl);
				const loginURL = joinPath(application.recognizeOrigin, `users/sign_in_start?redirect_to=${redirectParam}`);

				window.location.assign(loginURL);

				return;
			}

			useIdentityStore.setState({ token: jwt });
		})();
	}, [application, providedJWT]);

	const { data } = useProfileQuery();

	useEffect(() => {
		if (!token) return;

		const { companyId, spoofingUserId, userId } = parseToken(token);
		const tokenScope = userId || companyId || spoofingUserId ? [userId, companyId, spoofingUserId].join(':') : token;
		const previousTokenScope = tokenScopeRef.current;

		tokenScopeRef.current = tokenScope;

		if (!previousTokenScope || previousTokenScope === tokenScope) return;

		useIdentityStore.setState({
			areFeaturesFetched: false,
			isCompanyFetched: false,
			isUserFetched: false,
		});

		void getQueryClient().then((queryClient) => {
			const currentToken = useIdentityStore.getState().token;
			queryClient.removeQueries({
				predicate: (query) => {
					if (query.queryKey[0] !== meQueryKey[0]) return true;
					return query.queryKey[1] !== currentToken;
				},
			});
		});
	}, [token]);

	// Write /me data to Zustand store so hooks/getters can read from it
	useEffect(() => {
		if (!data) return;

		const { company, features, user } = data;
		const config = createConfig(application);

		useIdentityStore.setState({
			...config,
			company,
			features,
			isCompanyFetched: true,
			isUserFetched: true,
			areFeaturesFetched: true,
			user,
		});
	}, [data, application]);

	// Initialize analytics once Zustand is populated so hook values are accurate.
	// isCompanyFetched transitions false → true after the effect above, which means
	// isBizyEnabledForUser is correct when this effect fires.
	useEffect(() => {
		if (!data || !isCompanyFetched || analyticsInitRef.current) return;

		analyticsInitRef.current = true;

		const { company, user } = data;
		const hideLauncher = isBizyEnabledForUser;

		initializeAnalytics({ company, user });
		initializeIntercom({ company, user, isMobile, hideLauncher });
		initializeHotJar({ company, user });
	}, [data, isCompanyFetched, isBizyEnabledForUser, isMobile]);

	return <>{children}</>;
};
